import {
  Autocomplete,
  Card,
  CardContent,
  debounce,
  IconButton,
  InputAdornment,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { identity, isNullish } from 'remeda';
import { DynamicIcon, Tooltip } from 'src/components';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { Client, MergeClient } from 'src/graphql';
import { useSearch } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import { useScrollPaginateHandler } from 'src/utils/hooks';

interface ClientSelectorProps {
  client?: MergeClient | null;
  filters?: MergeClient[];
  deletable?: boolean;
  title?: string;
  partnerId?: number | null;
  setClient?: (client: Client | null) => void;
}

export interface ClientOption {
  id: string | null;
  label: string;
}

export const ClientSelector = ({
  client,
  deletable,
  title,
  filters,
  partnerId,
  setClient,
}: ClientSelectorProps) => {
  const [value, setValue] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const theme = useTheme();

  const clientIdsString = filters?.map((c) => c.clientId).join(',');

  const clientFilters = useMemo<ConditionInput[]>(() => {
    const list = [];

    if (!isNullish(partnerId)) {
      list.push({
        attribute: 'partnerId',
        operator: '=',
        value: partnerId.toString(),
      });
    }

    if (clientIdsString) {
      list.push({
        attribute: 'clientId',
        operator: 'not_in',
        value: clientIdsString,
      });
    }

    return list;
  }, [partnerId, clientIdsString]);

  const {
    records: clients,
    loadMore,
    isLoading,
    hasMore,
  } = useSearch({
    entityName: EntityNames.Client,
    searchTerm,
    filter: clientFilters,
  }) ?? {};

  const handleScroll = useScrollPaginateHandler({
    loadMore,
    enabled: hasMore,
    thresholdPx: 200,
  });

  const filteredClients = useMemo(
    () =>
      clients?.map((client) => ({
        label: client.fullName,
        id: client.clientId,
      })) || [],
    [clients],
  );

  const handleOnDelete = useCallback(() => {
    setClient?.(null);
  }, [setClient]);

  const onInputChange = debounce(
    ({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearchTerm(target.value);
    },
    SEARCH_DEBOUNCE_DELAY,
  );

  return client ? (
    <Card>
      <CardContent>
        <Typography
          gutterBottom
          variant="overline"
          borderBottom="1px solid"
          borderColor={theme.palette.custom.OV.sys.outlineVariant}
          color={theme.palette.custom.OV.sys.outline}
          display="block"
        >
          {title}
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="start">
          <Typography variant="caption" color={theme.palette.custom.OV.sys.onSurface}>
            {client?.fullName} | {client?.clientId}
          </Typography>
          {deletable ? (
            <Tooltip title="Delete">
              <IconButton color="error" onClick={handleOnDelete}>
                <DynamicIcon name="delete_forever" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  ) : (
    <Autocomplete
      autoHighlight
      value={value}
      loading={isLoading}
      fullWidth
      onChange={(_, clientToBeSelected) => {
        if (clientToBeSelected) {
          const currentClient =
            clients?.find((client) => client.clientId === clientToBeSelected.id) ?? null;
          setClient?.(currentClient);
          setValue(null);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search & Select Client"
          autoFocus
          onChange={onInputChange}
          InputProps={{
            ...params.InputProps,
            style: {
              padding: '0.375rem 1rem',
              borderRadius: '1rem',
            },
            startAdornment: (
              <InputAdornment position="start">
                <DynamicIcon name="search" />
              </InputAdornment>
            ),
            endAdornment: null,
          }}
        />
      )}
      options={filteredClients}
      renderOption={(props, option) => (
        <ListItem {...props} key={option.id}>
          <ListItemText primary={option.label} />
        </ListItem>
      )}
      ListboxProps={{
        onScroll: handleScroll,
      }}
      filterOptions={identity}
    />
  );
};
